import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { GetLoyaltyModel } from '../models/GetLoyaltyModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class LoyaltiesService {
  /**
   * Validate KocAilem
   * Validates loyalty for koc ailem by number and type (TCKN, KARTNO, ALTINKOD)
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateKocAilem(
    {
      number,
      type,
      basketId,
    }: {
      number?: string;
      type?: any;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/loyalties/validate/kocailem`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Zubizu
   * Validates loyalty for zubizu by code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateZubizu(
    {
      code,
      basketId,
    }: {
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/loyalties/validate/zubizu`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Hopi
   * Validates loyalty for hopi by item and type (CARDNO, TOKEN)
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateHopi(
    {
      item,
      type,
      basketId,
    }: {
      item?: string;
      type?: any;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/loyalties/validate/hopi`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(item ? { item: item } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate TurkTelekom
   * Validates loyalty for turk telekom by number and code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateTurkTelekom(
    {
      number,
      code,
      basketId,
    }: {
      number?: string;
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/loyalties/validate/turktelekom`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate TurkTelekom Prime
   * Validates loyalty for turk telekom prime by number and code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateTurkTelekomPrime(
    {
      number,
      code,
      basketId,
    }: {
      number?: string;
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/loyalties/validate/turktelekomprime`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate TurkTelekom Business Prime
   * Validates loyalty for turk telekom business prime by number and code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateTurkTelekomBusinessPrime(
    {
      number,
      code,
      basketId,
    }: {
      number?: string;
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/loyalties/validate/turktelekombusinessprime`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Paro
   * Validates loyalty for paro by number
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateParo(
    {
      number,
      basketId,
    }: {
      number?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/loyalties/validate/paro`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
